import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useThemeMode } from '../hooks/useThemeMode';
import { APP_VERSION, APP_ENV } from '../config';
import type { useAuth } from '../auth/useAuth';

type Auth = ReturnType<typeof useAuth>;

export function Layout({ children, auth }: { children: ReactNode; auth: Auth }) {
  const { mode, toggle } = useThemeMode();

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          padding: '0.75rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--bg-elev)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <Logo />
          <strong style={{ fontSize: '1rem' }}>Mockaroo Bulk</strong>
          {APP_ENV !== 'production' && (
            <span className="tag" style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}>
              {APP_ENV}
            </span>
          )}
        </div>

        <nav style={{ display: 'flex', gap: '1rem' }}>
          <NavTab to="/generate">Generate</NavTab>
          <NavTab to="/templates">Templates</NavTab>
        </nav>

        <div className="spacer" />

        <button onClick={toggle} title="Toggle theme" aria-label="Toggle theme">
          {mode === 'dark' ? '☀️' : '🌙'}
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: '0.85em' }}>
          <span>{auth.user?.name}</span>
          <span className="muted">
            {auth.user?.email} · {auth.user?.appRole}
          </span>
        </div>
        {!auth.isDemoMode && (
          <button onClick={() => auth.signOut()}>Sign out</button>
        )}
      </header>

      <main style={{ flex: 1, padding: '1.5rem', maxWidth: 1200, width: '100%', margin: '0 auto' }}>
        {children}
      </main>

      <footer
        style={{
          padding: '0.5rem 1.5rem',
          borderTop: '1px solid var(--border-color)',
          color: 'var(--text-muted)',
          fontSize: '0.8em',
          display: 'flex',
          gap: '1rem',
        }}
      >
        <span>Mockaroo Bulk · 8x8 Internal</span>
        <span className="spacer" />
        <span>{APP_VERSION}</span>
      </footer>
    </div>
  );
}

function NavTab({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        padding: '0.4rem 0.8rem',
        borderRadius: 6,
        textDecoration: 'none',
        background: isActive ? 'var(--card-border)' : 'transparent',
        color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
        fontWeight: 500,
      })}
    >
      {children}
    </NavLink>
  );
}

function Logo() {
  return (
    <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="6" fill="var(--bg-app)" />
      <path d="M7 9h18v3H7zM7 14.5h18v3H7zM7 20h12v3H7z" fill="var(--accent-lime)" />
      <circle cx="23" cy="21.5" r="3.5" fill="var(--accent)" />
    </svg>
  );
}
